import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';

@Entity()
export class SuppressionResult extends AdornisEntity {
  static override _class = 'SuppressionResult';

  @Field(type => Boolean) suppressed!: boolean;
  @Field(type => String) email!: string;
}

@Entity()
export class SuppressionCountResult extends AdornisEntity {
  static override _class = 'SuppressionCountResult';

  @Field(type => Int) suppressedCount!: number;
  @Field(type => [String]) suppressedEmails!: string[];
}

const COLLECTION = 'suppressed_contacts';
const normalize = (e: string) => e.trim().toLowerCase();

export const suppressContact = registerMutation({
  operationName: 'suppressContact',
  type: () => Boolean,
  params: [{ name: 'email', type: () => String }],
  resolve: (email: string) => async () => {
    const normalized = normalize(email);
    if (!normalized) throw new Error('Email is required');
    const col = await getRawCollection(COLLECTION);
    const existing = await col.findOne({ email: normalized });
    if (existing) return true;
    await col.insertOne({
      _id: crypto.randomUUID() as any,
      email: normalized,
      date: new Date().toISOString(),
    });
    return true;
  },
});

export const isEmailSuppressed = registerQuery({
  operationName: 'isEmailSuppressed',
  type: () => SuppressionResult,
  params: [{ name: 'email', type: () => String }],
  resolve: (email: string) => async (_gqlFields: any) => {
    const normalized = normalize(email);
    if (!normalized) return { suppressed: false, email } as any;
    const col = await getRawCollection(COLLECTION);
    const existing = await col.findOne({ email: normalized });
    return { suppressed: !!existing, email: normalized } as any;
  },
});

export const countSuppressedEmails = registerQuery({
  operationName: 'countSuppressedEmails',
  type: () => SuppressionCountResult,
  params: [{ name: 'emails', type: () => [String] }],
  resolve: (emails: string[]) => async (_gqlFields: any) => {
    const normalized = (emails ?? []).map(normalize).filter(Boolean);
    if (normalized.length === 0) return { suppressedCount: 0, suppressedEmails: [] };
    const col = await getRawCollection(COLLECTION);
    const docs = await col.find({ email: { $in: normalized } }).toArray();
    const suppressedEmails = docs.map((d: any) => d.email as string);
    return { suppressedCount: suppressedEmails.length, suppressedEmails };
  },
});
